// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Route, ClassId, MatrixCountry } from '../types';
import { esc, num, money, fmtValue, flag, shortName, countryColour } from '../format';
import { navigate } from '../main';

export function renderExplorer(root: HTMLElement, d: Dataset, route: Route): void {
  const order = d.meta.classOrder;
  let sortKey: ClassId | 'name' | 'present' = 'present';
  let asc = false;
  let q = route.filter ?? '';

  const cellVal = (cn: MatrixCountry, cid: ClassId): number => cn.byClass[cid]?.value ?? -1;
  const cellStr = (cn: MatrixCountry, cid: ClassId): string => {
    const cell = cn.byClass[cid];
    if (!cell) return '<span class="mini-note">—</span>';
    if (cid === 'commercial' && cell.dollars != null) return money(cell.dollars);
    return fmtValue(cid, cell.value);
  };

  function rowsHtml(): string {
    const term = q.trim().toLowerCase();
    let list = d.matrix.countries.slice();
    if (term) list = list.filter((c) => c.name.toLowerCase().includes(term) || shortName(c.name).toLowerCase().includes(term));
    list.sort((a, b) => {
      let av: number | string, bv: number | string;
      if (sortKey === 'name') { av = a.name; bv = b.name; }
      else if (sortKey === 'present') { av = a.present; bv = b.present; }
      else { av = cellVal(a, sortKey); bv = cellVal(b, sortKey); }
      const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : (av as number) - (bv as number);
      return asc ? cmp : -cmp;
    });
    if (!list.length) return `<tr><td colspan="${order.length + 2}"><p class="empty-state">No source country matches “${esc(q)}”.</p></td></tr>`;
    return list.map((cn) => `
      <tr class="clickable" data-country="${esc(cn.name)}">
        <td class="region-name"><span class="cdot" style="background:${countryColour(cn.iso, cn.name)}"></span><span class="flagemoji">${flag(cn.iso)}</span>${esc(shortName(cn.name))}</td>
        <td class="right num">${cn.present}</td>
        ${order.map((cid) => `<td class="right num">${cellStr(cn, cid)}</td>`).join('')}
      </tr>`).join('');
  }

  function refresh(): void {
    (root.querySelector('tbody') as HTMLElement).innerHTML = rowsHtml();
    root.querySelectorAll<HTMLElement>('tbody tr[data-country]').forEach((tr) => tr.addEventListener('click', () => navigate({ country: tr.dataset.country })));
    root.querySelectorAll<HTMLElement>('th[data-sort]').forEach((th) => {
      const k = th.dataset.sort!;
      th.setAttribute('aria-sort', k === sortKey ? (asc ? 'ascending' : 'descending') : 'none');
      th.innerHTML = th.textContent!.replace(/[▲▼]/g, '').trim() + (k === sortKey ? (asc ? ' ▲' : ' ▼') : '');
    });
  }

  root.innerHTML = `
    <div class="view-head">
      <h2>Explorer — every source country</h2>
      <p>All ${num(d.matrix.countries.length)} source countries across the six asset classes. Search, sort by any column, and click a country for its full profile. Values use each class's own unit and cannot be summed across columns.</p>
    </div>
    <div class="controls">
      <input type="search" id="q" placeholder="Search a country…" value="${esc(q)}" aria-label="Search country" />
    </div>
    <div class="panel table-scroll">
      <table>
        <thead><tr>
          <th class="sortable" data-sort="name">Country</th>
          <th class="sortable right" data-sort="present">Classes</th>
          ${order.map((cid) => `<th class="sortable right" data-sort="${cid}">${esc(d.meta.classMeta[cid].short)}</th>`).join('')}
        </tr></thead>
        <tbody></tbody>
      </table>
    </div>
    <p class="mini-note" style="margin-top:8px">Commercial is shown by value ($); residential is a yearly flow of acquisitions; farmland is hectares, water is gigalitres, business and mining are counts of registered interests.</p>
  `;

  const input = root.querySelector('#q') as HTMLInputElement;
  let t: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(t);
    t = window.setTimeout(() => { q = input.value; navigate({ filter: q || undefined }, true); refresh(); }, 250);
  });
  root.querySelectorAll<HTMLElement>('th[data-sort]').forEach((th) => th.addEventListener('click', () => {
    const k = th.dataset.sort as typeof sortKey;
    if (k === sortKey) asc = !asc; else { sortKey = k; asc = k === 'name'; }
    refresh();
  }));
  refresh();
}
